import axios from "axios";
import { getToken } from "./tokenHelper";

const CREATE_MISSING = true;

// SharePoint limits: 256 characters for file/folder names
const MAX_NAME_LENGTH = 256;

/* ----------------- utils ----------------- */
function enc(path: string): string {
  return path
    .split("/")
    .filter(Boolean)
    .map(encodeURIComponent)
    .join("/");
}
function dec(s: string) {
  return decodeURIComponent(s);
}

/**
 * GET an existing file by exact name inside a parent folder. Returns the
 * Graph driveItem (id, name, webUrl, size, parentReference) or null when
 * no file with that name lives there. Used after a 409 conflict so we can
 * surface the EXISTING file's refs to the caller — without that, "skipped"
 * uploads have nothing to point downstream tools (FDD queue, IRL match) at.
 */
async function fetchExistingByName(
  driveId: string,
  parentItemId: string,
  name: string,
  token: string,
): Promise<any | null> {
  const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}:/${enc(
    name,
  )}?$select=id,name,webUrl,size,parentReference`;
  const res = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    validateStatus: () => true,
  });
  if (res.status === 200 && res.data?.id) return res.data;
  return null;
}

/**
 * Standard shape for a "skipped because duplicate" result. The `existing`
 * fields mirror what we'd return on a successful upload (id, webUrl, size,
 * parentReference.driveId), so the upload router and downstream consumers
 * can use the same code path for both cases.
 */
function makeSkippedResult(args: {
  reason: "duplicate-semantic" | "duplicate-409";
  attemptedName: string;
  existing: any | null;
  driveId: string;
}) {
  const { reason, attemptedName, existing, driveId } = args;
  const message =
    reason === "duplicate-semantic"
      ? `⚠️ File with same number + name as "${attemptedName}" exists — upload skipped.`
      : `⚠️ File "${attemptedName}" already exists (409) — upload canceled.`;
  return {
    success: false,
    skipped: true as const,
    reason,
    message,
    // Existing-file refs (may be null if Graph fetch failed). Mirrors the
    // shape of a successful upload's driveItem so the router can read
    // .id / .webUrl / .size / .parentReference.driveId uniformly.
    id: existing?.id,
    name: existing?.name ?? attemptedName,
    webUrl: existing?.webUrl,
    size: existing?.size,
    parentReference: existing?.parentReference ?? { driveId },
  };
}

/**
 * Sanitize file name by:
 * 1. Replacing SharePoint invalid characters (\ / : * ? " < > | #) with "-"
 * 2. Truncating to maxLength characters (from the right, preserving leading code + extension)
 *
 * The leading numeric code (e.g., "1.2.3") is NEVER cut — only the descriptive part is shortened.
 */
function sanitizeFileName(name: string, maxLength: number = MAX_NAME_LENGTH): string {
  if (!name) return name;

  // Replace invalid characters
  let sanitized = name.replace(/[\/\\:*?"<>|#]/g, '-').trim();

  if (sanitized.length > maxLength) {
    // Extract extension
    const lastDot = sanitized.lastIndexOf('.');
    const hasExtension = lastDot > 0 && lastDot < sanitized.length - 1;
    const ext = hasExtension ? sanitized.substring(lastDot) : "";
    const base = hasExtension ? sanitized.substring(0, lastDot) : sanitized;

    // Extract leading code prefix (e.g., "1.2.3" from "1.2.3 Document Title")
    const codeMatch = base.match(/^\s*(\d+(?:\.\d+)*)/);
    const codePrefix = codeMatch ? codeMatch[0] : "";

    const maxBase = maxLength - ext.length;
    if (maxBase <= codePrefix.length) {
      // Code + extension already at/over limit — just use code + ext
      sanitized = codePrefix.trim() + ext;
    } else {
      sanitized = base.substring(0, maxBase).trim() + ext;
    }

    console.warn(`   ✂️ File name shortened to ${sanitized.length} chars: "${sanitized}"`);
  }

  return sanitized;
}

/* ----------------- Graph helpers ----------------- */
async function getJson(url: string, token: string, ok = [200]) {
  const r = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    validateStatus: () => true,
  });
  if (!ok.includes(r.status)) {
    throw new Error(`GET ${url} -> ${r.status}: ${JSON.stringify(r.data)}`);
  }
  return r.data;
}

async function getItemByPath(driveId: string, path: string, token: string) {
  if (!path) return null;
  const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${enc(
    path
  )}`;
  const r = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    validateStatus: () => true,
  });
  if (r.status === 200) return r.data;
  if (r.status === 404) return null;
  throw new Error(`GET ${path} -> ${r.status}: ${JSON.stringify(r.data)}`);
}

/** Exact existence by root-relative path. Returns true if file exists. */
async function fileExistsAtPath(
  driveId: string,
  itemPath: string,
  token: string
): Promise<boolean> {
  const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${enc(
    itemPath
  )}`;
  const r = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    validateStatus: () => true,
  });
  if (r.status === 200) return true;
  if (r.status === 404) return false;
  throw new Error(
    `Existence check failed (${r.status}): ${JSON.stringify(r.data)}`
  );
}

/** Exact existence by parent item id + name. Returns true if file exists. */
async function fileExistsUnderParent(
  driveId: string,
  parentItemId: string,
  name: string,
  token: string
): Promise<boolean> {
  const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}:/${encodeURIComponent(
    name
  )}`;
  const r = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    validateStatus: () => true,
  });
  if (r.status === 200) return true;
  if (r.status === 404) return false;
  throw new Error(
    `Parent existence check failed (${r.status}): ${JSON.stringify(r.data)}`
  );
}

async function createChild(
  driveId: string,
  parent: string,
  name: string,
  token: string
) {
  const url = parent
    ? `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${enc(
        parent
      )}:/children`
    : `https://graph.microsoft.com/v1.0/drives/${driveId}/root/children`;

  const r = await axios.post(
    url,
    { name, folder: {}, "@microsoft.graph.conflictBehavior": "fail" },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      validateStatus: () => true,
    }
  );

  if (r.status === 201) return parent ? `${parent}/${name}` : name;
  if (r.status === 409) return parent ? `${parent}/${name}` : name;
  throw new Error(`CREATE "${name}" -> ${r.status}: ${JSON.stringify(r.data)}`);
}

async function ensurePath(driveId: string, path: string, token: string) {
  const segs = path.split("/").filter(Boolean);
  let acc = "";
  for (const seg of segs) {
    const next = acc ? `${acc}/${seg}` : seg;
    const exists = await getItemByPath(driveId, next, token);
    if (!exists) {
      if (!CREATE_MISSING)
        throw new Error(`Missing folder: "${next}" (CREATE_MISSING=false)`);
      await createChild(driveId, acc, seg, token);
    }
    acc = next;
  }
  return acc;
}

/* ----------------- resolveFromUrl ----------------- */
type Resolved = {
  token: string;
  driveId: string;
  driveName: string;
  folderPath: string;
};

async function resolveFromUrl(folderUrl: string): Promise<Resolved> {
  const token = await getToken();
  const u = new URL(folderUrl);
  const host = u.hostname;

  const raw =
    u.searchParams.get("id") ||
    u.searchParams.get("RootFolder") ||
    u.searchParams.get("rootFolder");
  const serverRel = dec(raw ?? u.pathname);
  const parts = serverRel.split("/").filter(Boolean).map(dec);
  const si = parts.findIndex((p) =>
    ["sites", "teams"].includes(p.toLowerCase())
  );
  if (si === -1 || !parts[si + 1]) throw new Error("Cannot parse site from URL.");

  const siteName = parts[si + 1];
  let tail = parts.slice(si + 2);
  const fi = tail.findIndex((p) => p.toLowerCase() === "forms");
  if (fi >= 0) tail = tail.slice(0, fi);
  if (tail.length === 0)
    throw new Error("URL does not include a library/folder path after site.");

  const librarySegment = tail[0];
  const folderSegments = tail.slice(1);
  const site = await getJson(
    `https://graph.microsoft.com/v1.0/sites/${host}:/sites/${encodeURIComponent(
      siteName
    )}`,
    token
  );
  const siteId: string = site.id;
  const drivesData = await getJson(
    `https://graph.microsoft.com/v1.0/sites/${siteId}/drives?$select=id,name,webUrl`,
    token
  );
  const drives: Array<{ id: string; name: string; webUrl?: string }> =
    drivesData.value || [];

  const candidates = drives.filter((d) => {
    const nameMatch =
      d.name?.toLowerCase() === librarySegment.toLowerCase() ||
      (librarySegment.toLowerCase() === "shared documents" &&
        d.name?.toLowerCase() === "documents");
    const urlMatch = d.webUrl
      ? decodeURIComponent(d.webUrl)
          .toLowerCase()
          .includes(`/${encodeURIComponent(librarySegment).toLowerCase()}`)
      : false;
    return nameMatch || urlMatch;
  });
  if (candidates.length === 0)
    throw new Error(`Could not find library drive for "${librarySegment}".`);

  const drive =
    candidates.find(
      (d) => d.name?.toLowerCase() === librarySegment.toLowerCase()
    ) || candidates[0];

  const driveId = drive.id;
  const driveName = drive.name;
  const folderPath = folderSegments.join("/");
  return { token, driveId, driveName, folderPath };
}

/* ----------------- uploadFileToSharePoint ----------------- */
export async function uploadFileToSharePoint(
  file: File,
  folderUrl: string
): Promise<any> {
  const { token, driveId, folderPath } = await resolveFromUrl(folderUrl);

  // Sanitize the file name to ensure SharePoint compatibility
  const sanitizedFileName = sanitizeFileName(file.name);

  // Ensure the target folder path exists once up front.
  if (folderPath) await ensurePath(driveId, folderPath, token);
  const targetFolder = folderPath;

  // Semantic duplicate check (number + name), with pagination.
  const parseParts = (filename: string) => {
    const base = filename.replace(/\.[^/.]+$/, "");
    const match = base.match(/^([\d.\-]+)\s*[-_ ]*\s*(.*)$/); // "1.01 Budget", "1.01 - Budget", etc.
    return match
      ? {
          number: match[1].trim().toLowerCase(),
          name: match[2].trim().toLowerCase(),
        }
      : { number: "", name: base.trim().toLowerCase() };
  };
  const incoming = parseParts(sanitizedFileName);

  // ⛳ Run semantic scan ONLY if a leading number exists
  let semanticDuplicate = false;
  if (incoming.number) {
    let nextUrl =
      targetFolder && targetFolder.length > 0
        ? `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${enc(
            targetFolder
          )}:/children?$select=name&$top=200`
        : `https://graph.microsoft.com/v1.0/drives/${driveId}/root/children?$select=name&$top=200`;

    while (nextUrl) {
      const listRes = await axios.get(nextUrl, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: () => true,
      });
      if (listRes.status !== 200)
        throw new Error(
          `Failed to list existing files (${listRes.status})`
        );

      const names: string[] = (listRes.data?.value || []).map(
        (f: any) => f.name
      );
      if (
        names.some((n) => {
          const e = parseParts(n);
          return e.number === incoming.number && e.name === incoming.name;
        })
      ) {
        semanticDuplicate = true;
        break;
      }
      nextUrl = listRes.data?.["@odata.nextLink"] ?? "";
    }
  }

  if (semanticDuplicate) {
    return {
      success: false,
      skipped: true,
      reason: "duplicate-semantic",
      message: `⚠️ File with same number + name as "${sanitizedFileName}" exists — upload skipped.`,
    };
  }

  // Build exact item path and upload (fail on conflict, never rename)
  const itemPath = [targetFolder, sanitizedFileName].filter(Boolean).join("/");
  const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${enc(
    itemPath
  )}:/content?@microsoft.graph.conflictBehavior=fail`;

  const res = await axios.put(url, file, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": file.type || "application/octet-stream",
    },
    validateStatus: () => true,
  });

  if (res.status === 409) {
    // Duplicate by exact name (Graph conflict)
    return {
      success: false,
      skipped: true,
      reason: "duplicate-409",
      message: `⚠️ File "${sanitizedFileName}" already exists (409) — upload canceled.`,
    };
  }

  if (res.status >= 200 && res.status < 300) {
    return {
      success: true,
      skipped: false,
      message: `✅ Uploaded ${sanitizedFileName} successfully.`,
      data: res.data,
    };
  }

  throw new Error(`Upload failed (${res.status})`);
}

/* ----------------- renameFolderInSharePoint ----------------- */
export async function renameFolderInSharePoint(
  driveId: string,
  itemId: string,
  newName: string
): Promise<void> {
  const token = await getToken();
  const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}`;
  const res = await axios.patch(url, { name: newName }, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    validateStatus: () => true,
  });
  if (res.status === 200) {
    console.log(`   ✂️ Renamed folder to "${newName}"`);
    return;
  }
  // Non-fatal: log a warning but don't throw — better to keep the long name than crash
  console.warn(`   ⚠️ Rename failed (${res.status}): ${JSON.stringify(res.data?.error?.message || res.data || "")}`);
}

/* ----------------- Proportional Path Shortening ----------------- */

// Minimum name length — names at or below this floor are never shortened
const MIN_NAME_LENGTH = 20;
// SharePoint server-relative URL path limit
const PATH_CHAR_LIMIT = 400;
// Conservative estimate for /sites/{siteName}/{libraryName}/ base path
const BASE_PATH_ESTIMATE = 80;

type AncestorInfo = { itemId: string; name: string };

/**
 * Walk up the folder tree from an item, collecting ancestor folder info.
 * Returns ancestors ordered root → deepest (excluding the drive root itself).
 * If boundaryItemId is provided, stops walking when that folder is reached
 * (the boundary folder itself is excluded from the chain).
 */
async function getAncestorChain(
  driveId: string,
  itemId: string,
  token: string,
  boundaryItemId?: string
): Promise<AncestorInfo[]> {
  const chain: AncestorInfo[] = [];
  let currentId = itemId;

  for (let safety = 0; safety < 15; safety++) {
    // Stop if we've reached the boundary (user's selected root folder)
    if (boundaryItemId && currentId === boundaryItemId) break;

    const res = await axios.get(
      `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${currentId}?$select=id,name,parentReference`,
      { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true }
    );
    if (res.status !== 200 || !res.data) break;

    const item = res.data;
    const parentPath: string = item.parentReference?.path || "";

    // parentPath ends with "root:" means this item is directly under the drive root
    if (parentPath.endsWith("root:")) {
      chain.unshift({ itemId: item.id, name: item.name });
      break; // reached root level
    }

    chain.unshift({ itemId: item.id, name: item.name });
    if (!item.parentReference?.id) break;
    currentId = item.parentReference.id;
  }

  return chain;
}

/**
 * Proportionally shorten ancestor folder names to fit within SharePoint's ~400-char path limit.
 *
 * Algorithm:
 * 1. Walk up the tree to get the full ancestor chain
 * 2. Calculate how many chars of excess the path has
 * 3. Each ancestor's "reducible" portion = max(0, nameLength - MIN_NAME_LENGTH)
 * 4. Each ancestor loses (excess / totalReducible) × itsReducible chars
 * 5. Rename shortened ancestors via Graph API (non-fatal on failure)
 * 6. The leading code prefix (e.g., "4.2.47") is NEVER cut
 *
 * Pass renameAncestors=false to leave every folder name untouched and only
 * report the budget left for the child (Folder Mirroring matches folders by
 * name, so a renamed folder would be re-created as a duplicate next run).
 *
 * @returns Remaining character budget for the child name/file
 */
export async function shortenPathProportionally(
  driveId: string,
  parentItemId: string,
  childNameLength: number,
  boundaryItemId?: string,
  renameAncestors: boolean = true
): Promise<number> {
  const token = await getToken();

  // Folders stay as-is, so all we need is the room left for the file name. Measure
  // the real path SharePoint counts (the parent's decoded webUrl path) instead of
  // estimating the /sites/{site}/{library}/ prefix.
  if (!renameAncestors) {
    const res = await axios.get(
      `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}?$select=webUrl`,
      { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true }
    );
    if (res.status === 200 && res.data?.webUrl) {
      const parentPath = decodeURIComponent(new URL(res.data.webUrl).pathname);
      const budget = PATH_CHAR_LIMIT - parentPath.length - 1; // "/" before the file name
      console.log(`   📏 Parent path is ${parentPath.length} chars — ${budget} left for the file name (folders kept as-is).`);
      return Math.max(0, budget);
    }
    // Couldn't read the real path — fall through to the estimate below.
  }

  const ancestors = await getAncestorChain(driveId, parentItemId, token, boundaryItemId);

  if (ancestors.length === 0) return PATH_CHAR_LIMIT - BASE_PATH_ESTIMATE;

  const totalNamesLength = ancestors.reduce((sum, a) => sum + a.name.length, 0);
  const slashes = ancestors.length + 1; // between folders + before child
  const available = PATH_CHAR_LIMIT - BASE_PATH_ESTIMATE - slashes;
  const totalNeeded = totalNamesLength + childNameLength;
  const excess = totalNeeded - available;

  if (excess <= 0) {
    return available - totalNamesLength; // already fits
  }

  if (!renameAncestors) {
    console.log(`   📏 Path exceeds estimated limit by ${excess} chars. Keeping folder names; shortening the file name instead.`);
    return Math.max(0, available - totalNamesLength);
  }

  console.log(`   📏 Path exceeds limit by ${excess} chars. Proportionally shortening ${ancestors.length} ancestor(s)…`);

  // Calculate reducible chars per ancestor.
  // The floor for each ancestor is the GREATER of MIN_NAME_LENGTH or its code prefix length,
  // because the code prefix (e.g., "1.2.3.4.5.6.7.8.9.10.11") is NEVER shortened.
  const codeRegex = /^\s*(\d+(?:\.\d+)*)/;
  const reducible = ancestors.map(a => {
    const codeMatch = a.name.match(codeRegex);
    const codePrefixLen = codeMatch ? codeMatch[0].trim().length : 0;
    const floor = Math.max(MIN_NAME_LENGTH, codePrefixLen);
    return Math.max(0, a.name.length - floor);
  });
  const totalReducible = reducible.reduce((sum, r) => sum + r, 0);

  if (totalReducible === 0) {
    console.warn(`   ⚠️ All ancestors already at minimum (${MIN_NAME_LENGTH} chars). Cannot shorten further.`);
    return Math.max(0, available - totalNamesLength);
  }

  const cutRatio = Math.min(1, excess / totalReducible);
  let savedTotal = 0;

  for (let i = 0; i < ancestors.length; i++) {
    const a = ancestors[i];
    const toRemove = Math.ceil(reducible[i] * cutRatio);
    if (toRemove <= 0) continue;

    // Preserve leading code prefix — compute per-ancestor floor
    const codeMatch = a.name.match(codeRegex);
    const codePrefix = codeMatch ? codeMatch[0].trim() : "";
    const floor = Math.max(MIN_NAME_LENGTH, codePrefix.length);

    const targetLength = Math.max(floor, a.name.length - toRemove);
    if (targetLength >= a.name.length) continue;

    let newName: string;
    if (targetLength <= codePrefix.length) {
      newName = codePrefix; // just the code, never cut it
    } else {
      newName = a.name.substring(0, targetLength).trim();
    }

    if (newName === a.name || newName.length >= a.name.length) continue;

    console.log(`   ✂️ "${a.name}" → "${newName}" (saved ${a.name.length - newName.length} chars)`);
    await renameFolderInSharePoint(driveId, a.itemId, newName);
    savedTotal += (a.name.length - newName.length);
  }

  const remaining = available - (totalNamesLength - savedTotal);
  console.log(`   📏 Freed ${savedTotal} chars. Remaining budget for child: ${remaining}`);
  return Math.max(0, remaining);
}

/* ----------------- createFolderInSharePoint ----------------- */
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

// Shared cooldown gate for folder creates. When any in-flight create hits a
// 429, it pushes coolingUntil into the future; every other create checks the
// gate before sending and sleeps until it expires. Stops sibling workers from
// hammering the rate limiter while one of them is already being throttled.
let coolingUntil = 0;

async function awaitCreateCooldown(): Promise<void> {
  const waitMs = coolingUntil - Date.now();
  if (waitMs > 0) {
    console.warn(`   ⏸️ Folder-create cooldown active, waiting ${waitMs}ms…`);
    await new Promise((resolve) => setTimeout(resolve, waitMs));
  }
}

function pushCreateCooldown(retryAfterMs: number): void {
  const newUntil = Date.now() + retryAfterMs;
  if (newUntil > coolingUntil) {
    coolingUntil = newUntil;
    console.warn(`   ⏸️ Folder-create rate-limit hit — pausing all creates for ${retryAfterMs}ms`);
  }
}

export async function createFolderInSharePoint(
  driveId: string,
  parentItemId: string,
  displayName: string
): Promise<string> {
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    await awaitCreateCooldown();
    const token = await getToken();
    const createUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}/children`;
    const res = await axios.post(
      createUrl,
      { name: displayName, folder: {}, "@microsoft.graph.conflictBehavior": "fail" },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        validateStatus: () => true,
      }
    );

    if (res.status === 201 && res.data?.id) return res.data.id as string;
    if (res.status === 409) {
      const listUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}/children?$select=id,name,folder&$top=999`;
      const listRes = await axios.get(listUrl, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: () => true,
      });
      const wanted = displayName.toLowerCase();
      const hit = (listRes.data?.value || []).find(
        (c: any) => c?.folder && String(c.name).toLowerCase() === wanted
      );
      if (hit?.id) return hit.id as string;
      throw new Error(`Folder "${displayName}" exists but could not be resolved.`);
    }

    // Don't retry permanent 400 errors (path too long, invalid name, etc.)
    const errMsg = String(res.data?.error?.message || res.data || "").toLowerCase();
    if (res.status === 400 && (errMsg.includes("name") || errMsg.includes("path") || errMsg.includes("length") || errMsg.includes("invalid"))) {
      throw new Error(`Create folder failed (${res.status}): ${JSON.stringify(res.data?.error?.message || res.data || "")}`);
    }

    // Retry on transient 400/429/5xx errors
    if ((res.status === 400 || res.status === 429 || res.status >= 500) && attempt < MAX_RETRIES) {
      if (res.status === 429) {
        const retryAfterMs = parseInt(res.headers?.["retry-after"] || "2", 10) * 1000;
        // Push the shared cooldown so sibling workers also pause; the next
        // loop iteration's awaitCreateCooldown handles the wait for this one.
        pushCreateCooldown(retryAfterMs);
        console.warn(`   ⚠️ Create folder got 429, cooldown set to ${retryAfterMs}ms (attempt ${attempt}/${MAX_RETRIES})`);
      } else {
        const delay = RETRY_DELAY_MS * attempt;
        console.warn(`   ⚠️ Create folder got ${res.status}, retrying in ${delay}ms (attempt ${attempt}/${MAX_RETRIES})…`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      continue;
    }

    throw new Error(`Create folder failed (${res.status}): ${JSON.stringify(res.data?.error?.message || res.data || "")}`);
  }

  throw new Error(`Create folder failed after ${MAX_RETRIES} attempts`);
}

/* ----------------- fileExistsInFolder ----------------- */
/**
 * Live check: is a file with this name directly inside the folder right now?
 * Unclear answers (throttling, server errors) count as "exists" so callers
 * never upload on a guess.
 */
export async function fileExistsInFolder(
  driveId: string,
  parentItemId: string,
  fileName: string
): Promise<boolean> {
  const token = await getToken();
  const res = await axios.get(
    `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}:/${encodeURIComponent(fileName)}?$select=id`,
    { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true }
  );
  return res.status !== 404;
}

/* ----------------- uploadFileToParentItem ----------------- */
export async function uploadFileToParentItem(
  driveId: string,
  parentItemId: string,
  file: File,
  onProgress?: (fraction: number) => void,
  providerId?: string,
  rootItemId?: string,
  renameFolders: boolean = true // false: on a too-long path, shorten only the file name
): Promise<any> {
  const token = await getToken();
  const FOUR_MB = 4 * 1024 * 1024;

  // Sanitize the file name to ensure SharePoint compatibility
  let sanitizedFileName = sanitizeFileName(file.name);

  // Semantic duplicate check (number + name), paginated
  const parseParts = (filename: string) => {
    const base = filename.replace(/\.[^/.]+$/, "");
    const match = base.match(/^([\d.\-]+)\s*[-_ ]*\s*(.*)$/);
    return match
      ? {
          number: match[1].trim().toLowerCase(),
          name: match[2].trim().toLowerCase(),
        }
      : { number: "", name: base.trim().toLowerCase() };
  };
  const incoming = parseParts(sanitizedFileName);

  /**
   * Firmex-specific duplicate matching.
   * Matches if EITHER:
   * 1. Exact code match: "2" === "2"
   * 2. Relative code match: "2" === last segment of "1.2"
   */
  const firmexMatches = (incomingNum: string, existingNum: string): boolean => {
    // Exact match
    if (incomingNum === existingNum) return true;

    // Relative match: extract last segment after last dot
    if (existingNum.includes(".")) {
      const lastSegment = existingNum.split(".").pop() || "";
      if (incomingNum === lastSegment) return true;
    }

    return false;
  };

  // ⛳ Run semantic scan ONLY if a leading number exists.
  // We $select id, webUrl, size, parentReference (in addition to name) so
  // that a hit lets us return the EXISTING file's SharePoint refs to the
  // caller — that way "Skipped because duplicate" still surfaces a usable
  // {driveId, itemId, webUrl} pair for downstream consumers (the FDD queue
  // promotion in particular).
  if (incoming.number) {
    let nextUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}/children?$select=id,name,webUrl,size,parentReference&$top=200`;
    while (nextUrl) {
      const listRes = await axios.get(nextUrl, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: () => true,
      });
      if (listRes.status !== 200)
        throw new Error(`Failed to list children (${listRes.status})`);

      const items: any[] = listRes.data?.value || [];

      const matched = items.find((item) => {
        if (!item?.name) return false;
        const e = parseParts(item.name);
        if (providerId === "firmex") {
          return firmexMatches(incoming.number, e.number) && incoming.name === e.name;
        }
        return e.number === incoming.number && e.name === incoming.name;
      });

      if (matched) {
        console.warn(`   🔍 SKIP (semantic): "${sanitizedFileName}" matched existing "${matched.name}" (number="${incoming.number}", name="${incoming.name}")`);
        return makeSkippedResult({
          reason: "duplicate-semantic",
          attemptedName: sanitizedFileName,
          existing: matched,
          driveId,
        });
      }
      nextUrl = listRes.data?.["@odata.nextLink"] ?? "";
    }
  }

  // ---- Small file: fail on conflict ----
  if (file.size <= FOUR_MB) {
    const doSmallUpload = async (name: string) => {
      const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}:/${enc(
        name
      )}:/content?@microsoft.graph.conflictBehavior=fail`;
      return axios.put(url, file, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": file.type || "application/octet-stream",
        },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) onProgress(evt.loaded / evt.total);
        },
        validateStatus: () => true,
      });
    };

    let res = await doSmallUpload(sanitizedFileName);

    if (res.status === 409) {
      console.warn(`   🔍 SKIP (small-file 409): "${sanitizedFileName}" got 409 on direct upload`);
      const existing = await fetchExistingByName(driveId, parentItemId, sanitizedFileName, token);
      return makeSkippedResult({
        reason: "duplicate-409",
        attemptedName: sanitizedFileName,
        existing,
        driveId,
      });
    }
    if (res.status >= 200 && res.status < 300) {
      onProgress?.(1);
      return res.data;
    }

    // Check if path/name too long — proportionally shorten ancestors and retry
    const errMsg = String(res.data?.error?.message || "").toLowerCase();
    const isPathError = res.status === 400 &&
      (errMsg.includes("name") || errMsg.includes("path") || errMsg.includes("length") || errMsg.includes("too long"));

    if (isPathError) {
      const budget = await shortenPathProportionally(driveId, parentItemId, sanitizedFileName.length, rootItemId, renameFolders);
      if (budget > 0 && budget < sanitizedFileName.length) {
        sanitizedFileName = sanitizeFileName(file.name, budget);
        console.warn(`   ✂️ Retrying upload with budget-fit name (${budget}): "${sanitizedFileName}"`);
      }
      res = await doSmallUpload(sanitizedFileName);
      if (res.status === 409) {
        const existing = await fetchExistingByName(driveId, parentItemId, sanitizedFileName, token);
        return makeSkippedResult({
          reason: "duplicate-409",
          attemptedName: sanitizedFileName,
          existing,
          driveId,
        });
      }
      if (res.status >= 200 && res.status < 300) {
        onProgress?.(1);
        return res.data;
      }
    }

    throw new Error(
      `Simple upload failed (${res.status}): ${JSON.stringify(res.data)}`
    );
  }

  // ---- Large file: upload session ----
  const doCreateSession = async (name: string, conflictBehavior: string = "fail") => {
    const sessionUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}:/${enc(
      name
    )}:/createUploadSession`;
    return axios.post(
      sessionUrl,
      { item: { "@microsoft.graph.conflictBehavior": conflictBehavior, name } },
      {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        validateStatus: () => true,
      }
    );
  };

  let create = await doCreateSession(sanitizedFileName);

  if (create.status !== 200) {
    // 409 on session creation: could be an orphaned session from a previous incomplete upload.
    // Check if the file actually exists — if not, retry with "replace" to clear the stale session.
    if (create.status === 409) {
      // The semantic duplicate check already ran above. If we're here, the file
      // passed that check — so a 409 means a ghost/incomplete file or encoding
      // artifact, not a genuine duplicate. Always retry with "replace".
      console.log(`   🔄 Large-file 409 for "${sanitizedFileName}" (parentItemId=${parentItemId}). Retrying with replace…`);
      create = await doCreateSession(sanitizedFileName, "replace");
    }

    const errMsg = String(create.data?.error?.message || "").toLowerCase();
    const isPathError = create.status === 400 &&
      (errMsg.includes("name") || errMsg.includes("path") || errMsg.includes("length") || errMsg.includes("too long"));

    if (isPathError) {
      const budget = await shortenPathProportionally(driveId, parentItemId, sanitizedFileName.length, rootItemId, renameFolders);
      if (budget > 0 && budget < sanitizedFileName.length) {
        sanitizedFileName = sanitizeFileName(file.name, budget);
        console.warn(`   ✂️ Retrying session with budget-fit name (${budget}): "${sanitizedFileName}"`);
      }
      create = await doCreateSession(sanitizedFileName);
    }

    if (create.status !== 200) {
      throw new Error(
        `createUploadSession failed (${create.status}): ${JSON.stringify(create.data)}`
      );
    }
  }

  const uploadUrl: string = create.data?.uploadUrl;
  // Graph requires every non-final fragment to be a multiple of 320 KiB.
  // 10 MiB = 32 × 320 KiB. (8 MiB was not a multiple and got rejected.)
  const CHUNK = 10 * 1024 * 1024;
  let start = 0;
  while (start < file.size) {
    const end = Math.min(start + CHUNK, file.size);
    const chunk = file.slice(start, end);
    const contentRange = `bytes ${start}-${end - 1}/${file.size}`;
    const putChunk = () =>
      axios.put(uploadUrl, chunk, {
        // No Content-Length — the browser sets it and refuses overrides.
        headers: {
          "Content-Range": contentRange,
          "Content-Type": "application/octet-stream",
        },
        validateStatus: () => true,
      });
    let put;
    try {
      put = await putChunk();
    } catch (err: any) {
      // Network-level failure (CORS-masked error or dropped connection) — retry once.
      console.warn(`   ⚠️ Chunk ${contentRange} failed (${err?.message}); retrying once…`);
      try {
        put = await putChunk();
      } catch (err2: any) {
        throw new Error(`Chunk upload failed at ${contentRange}: ${err2?.message || String(err2)}`);
      }
    }
    if (put.status === 202) {
      start = end;
      onProgress?.(end / file.size);
      continue;
    }
    if (put.status === 200 || put.status === 201) {
      onProgress?.(1);
      return put.data;
    }
    throw new Error(
      `Chunk upload failed (${put.status}): ${JSON.stringify(put.data)}`
    );
  }

  throw new Error("Unexpected end of chunked upload loop.");
}
